// src/services/aiListingService.js
import { vertexAI, getGenerativeModel } from '../firebase';
import imageCompression from 'browser-image-compression';

// 1. PORTED SCHEMA (Matches your Dart Schema object)
const responseSchema = {
  type: "object",
  properties: {
    isValidListing: { type: "boolean" },
    rejectionReason: { type: "string", nullable: true },
    category: { 
        type: "string", 
        enum: ["Cars", "Bikes", "Electronics & Appliances", "Mobiles", "Furniture", "Properties", "Books Sports & Hobbies", "Fashion", "Services"] 
    },
    subCategory: { type: "string" },
    brand: { type: "string", nullable: true },
    model: { type: "string", nullable: true },
    year: { type: "number", nullable: true },
    fuelType: { type: "string", nullable: true },
    kmsDriven: { type: "string", nullable: true },
    bodyType: { 
        type: "string", 
        nullable: true,
        enum: ['Sedan', 'SUV', 'Hatchback', 'MUV', 'Coupe', 'Convertible', 'Pickup', 'Van']
    },
    condition: { type: "string", enum: ['Excellent', 'Good', 'Fair', 'Poor'] },
    features: { type: "array", items: { type: "string" } },
    titles: { type: "array", items: { type: "string" } },
    description: { type: "string" },
    confidence: { type: "number" },
    priceSuggested: { type: "number" },
    priceMin: { type: "number" },
    priceMax: { type: "number" }
  }
};

// 2. PORTED PROMPT v3.0
const PROMPT_V3 = `
Analyze this product image for an Indian marketplace.

**CRITICAL INSTRUCTIONS:**
1. Respond ONLY with valid JSON matching the schema.
2. Use null for visible fields you cannot detect.
3. If multiple images, combine clues.
4. If Commercial Vehicle (Truck/Bus), return null.

---

## **STEP 0: VALIDATION CHECK** (Priority High)
 Analyze if the image contains a valid physical product to sell.

  **Mark "isValidListing": false** if the image shows:
   - **Living Things:** Animals, Pets, Wild life (unless clearly wearing a product like a saddle/collar).
   - **People:** Selfies, Group photos (unless clearly modelling clothes/jewelry).
   - **Nature:** Sky, Clouds, Mountains, Trees, Grass (without a main product).
   - **Junk/Blur:** Wall, Ceiling, Floor, Darkness, Extreme Blur, Unidentifiable blobs.
   - **Restricted:** Weapons, Drugs, Nudity, Offensive content.

   **If Invalid:** 
   - Set "isValidListing": false
   - Set "rejectionReason": "This looks like [reason]. Please upload a clear photo of the item you want to sell."
   - Set all other fields (category, price, etc.) to null.

   **If Valid:**
   - Set "isValidListing": true
   - Set "rejectionReason": null
   - Proceed to categorize and fill all other fields.

---

## **STEP 1: STRICT CATEGORY MAPPING**
Map the visual to ONE Main Category and ONE Sub-Category string from the lists below.

1. **Cars** (4-wheelers)
   - *Sub-Category:* Output the **Brand Name** (e.g., Maruti Suzuki, Hyundai, Tata, Honda, Toyota, Mahindra, BMW, Mercedes-Benz, Audi, etc).

2. **Bikes** (2-wheelers)
   - *Sub-Category:* [Motorcycles, Scooters, Bicycles, Spare Parts]

3. **Electronics & Appliances**
   - *Sub-Category:* ['Accessories', 'Wearables', TVs & Video - Audio, Kitchen & Other Appliances, Computers & Laptops, Cameras & Lenses, Games & Entertainment, Fridges, Hard Disks Printers & Monitors, ACs, Washing Machines]

4. **Mobiles**
   - *Sub-Category:* [Mobile Phones, Tablets, Accessories (chargers, earphones)]

5. **Furniture**
   - *Sub-Category:* [Sofa & Dining, Beds & Wardrobes, Home Decor & Garden, Kids Furniture, Other Household Items]

6. **Properties**
   - *Sub-Category:* [Houses & Apartments, Lands & Plots, Shops & Offices, PG & Guest Houses]

7. **Books Sports & Hobbies**
   - *Sub-Category:* [Books, Gym & Fitness, Musical Instruments, Sports Equipment, Other Hobbies]

8. **Fashion**
   - *Sub-Category:* [Men's Wear, Women's Wear, Kids Fashion, Jewelry]

9. **Services**
   - *Sub-Category:* [Education & Classes, Tours & Travel, Electronics Repair & Services, Health & Beauty, Home Renovation & Repair, Cleaning & Pest Control, Packers & Movers, Other Services]

---

STEP 2: STRICT CATEGORY RULES (DO NOT VIOLATE)

Use these rules to resolve ambiguity:
	•	Chargers, earphones
    → Category: Mobiles
    → Sub-Category: Accessories
	•	Power banks, cables, headphones, keyboards, mouse
    → Category: Electronics & Appliances
    → Sub-Category: Accessories
	•	Smartwatch, fitness band, watch (any type)
    → Category: Electronics & Appliances
    → Sub-Category: Wearables
	•	Laptop, MacBook, desktop computer
    → Category: Electronics & Appliances
    → Sub-Category: Computers & Laptops
	•	Refrigerator
    → Category: Electronics & Appliances
    → Sub-Category: Fridges
	•	Air conditioner
    → Category: Electronics & Appliances
    → Sub-Category: ACs
  •	Playstation Consoles, XBox, Nintendo Switch and similar ones
    → Category: Electronics & Appliances
    → Sub-Category: Games & Entertainment

STEP 2.1: STRICT CATEGORY RULES (Fashion Expansion)

  1. Priority Rule: Kids, Babies & School Items
  Logic: If it fits a child (0-14 years), it must go here, even if it looks like a shirt or a shoe.
  Keywords: Baby, Infant, Toddler, Boy, Girl, Kids, School Uniform, Toys (clothing), Baby shoes, Kids sandals.
    → Category: Fashion
    → Sub-Category: Kids' Fashion
  
  2. Jewelry & Ornaments
  Logic: Precious metals, stones, and artificial ornaments.
  Keywords: Gold, Silver, Diamond, Ring, Necklace, Chain, Earring, Bangle, Bracelet, Anklet, Mangalsutra, Nose pin.
    → Category: Fashion
    → Sub-Category: Jewelry
  
  3. Women’s Fashion (Clothing, Shoes & Bags)
  Logic: Any clothing, footwear, or accessory explicitly for women.
  Clothing Keywords: Saree, Kurti, Lehenga, Salwar, Blouse, Dress, Gown, Skirt, Top, Leggings, Dupatta.
  Footwear Keywords: Heels, Wedges, Women's Flats, Women's Sandals, Stilettos.
  Accessory Keywords: Handbag, Purse, Clutch, Tote bag, Women's Watch.
    → Category: Fashion
    → Sub-Category: Women's Wear
  
  4. Men’s Fashion (Clothing, Shoes & Accessories)
  Logic: Any clothing, footwear, or accessory explicitly for men.
  Clothing Keywords: Shirt, T-shirt, Jeans, Trousers, Blazer, Suit, Tuxedo, Sherwani, Kurta Pajama, Dhoti, Lungi, Vest.
  Footwear Keywords: Men's Shoes, Sneakers, Formal Shoes, Loafers, Boots, Men's Sandals/Slippers.
  Accessory Keywords: Belt, Wallet, Tie, Cufflinks, Men's Watch.
    → Category: Fashion
    → Sub-Category: Men's Wear
  
  5. Distinguishing Watches vs Smartwatches
  Ambiguity Rule:
  "Apple Watch", "Galaxy Watch", "Fitness Band", "Garmin"
    → Category: Electronics & Appliances
    → Sub-Category: Wearables
  "Analog Watch", "Casio", "Titan", "Rolex", "Leather Strap Watch"
    → Category: Fashion
    → Sub-Category: Men's Wear OR Women's Wear (based on gender keywords)


If unsure, choose the closest valid sub-category.

---

## **STEP 3: ANALYSIS LOGIC**

### ✅ IF VEHICLE
- **Brand/Model:** Detect from logo/badging.
- **Year:** Estimate based on generation.
- **Fuel:** Guess (CRDi=Diesel, Green Plate=Electric, i-VTEC=Petrol).
- **Odometer:** READ exact value if visible.

### ✅ IF OTHERS
- Detect exact model, material, and type.
- Verify condition (Screens, Body, Wear).

---

## **STEP 4: CONTENT STYLE (STRICT)**

### ✅ **Condition**
Select strictly from Schema: [Excellent, Good, Fair, Poor] based on scratches/dents.

### ✅ **Key Features**
List **3–5 visible features** (e.g., "Alloy Wheels", "Touchscreen", "Leather Seats").

### ✅ **Title Options**
Generate **3 compelling titles**:
- 5–70 characters
- **No ALL CAPS**
- Marketplace optimized (e.g., "Maruti Swift 2018 VXI - Mint Condition")

### ✅ **Description**
- 20–500 characters.
- Honest resale description.
- Highlight condition & usage.
- **No false guarantees.**

### ✅ **Price Suggestion (INR)**
Estimate Min/Max/Suggested resale price for Used Indian Market.

### ✅ **Confidence**
0–100 (how confident category & model detection is)
`;


export class AIListingService {
  constructor() {
    this.model = getGenerativeModel(vertexAI, {
    model: "gemini-2.5-flash", generationConfig: {
    responseMimeType: "application/json",
    responseSchema: responseSchema,
    temperature: 0.1, // Strict factual response
  },
});;
  }

  async analyzeProduct(fileList) {
    try {
      const parts = [{ text: PROMPT_V3 }];

      // Process and compress each image
      for (const file of fileList) {
        const compressed = await this._compressImage(file);
        const base64Data = await this._fileToGenerativePart(compressed);
        parts.push(base64Data);
      }

      const result = await this.model.generateContent(parts);
      const response = await result.response;
      const text = response.text();
      
      const jsonData = JSON.parse(text);

      if (jsonData.isValidListing === false) {
        throw new Error(jsonData.rejectionReason || "Invalid product image.");
      }

      return jsonData;
    } catch (error) {
      console.error("AI Analysis Error:", error);
      throw error;
    }
  }

  async _compressImage(file) {
    const options = {
      maxSizeMB: 0.5, // Match your 500kb target
      maxWidthOrHeight: 800, // Match your Dart maxWidth
      useWebWorker: true,
    };
    return await imageCompression(file, options);
  }

  async _fileToGenerativePart(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve({
          inlineData: {
            data: reader.result.split(',')[1],
            mimeType: file.type,
          },
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}